import { ConfirmAction } from "@atlas/composite";
import {
  Form,
  FormInputField,
  Item,
  ItemContent,
  ItemMedia,
  ItemTitle,
  useToast,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import { zodResolver } from "@hookform/resolvers/zod";
import { useImportHubItem } from "@query";
import { parseHubTokenKey } from "@shared";
import { CheckCircle2 } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

export interface ImportTokenDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  title?: string;
  description?: string;
}

export function ImportTokenDialog({
  open,
  onOpenChange,
  onSuccess,
  title,
  description,
}: ImportTokenDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const formSchema = useMemo(
    () =>
      z.object({
        importToken: z.string().refine(
          (val) => !val || parseHubTokenKey(val) !== null,
          t("hub.package_manager.invalid_token", {
            defaultValue: "Invalid token key",
          }),
        ),
      }),
    [t],
  );

  const form = useForm({
    defaultValues: { importToken: "" },
    mode: "onChange",
    resolver: zodResolver(formSchema),
  });
  const importKeyInput = form.watch("importToken") || "";
  const { mutate: importHubItem, isPending: isImportPending } =
    useImportHubItem();

  const decodedImportInfo = useMemo(
    () => parseHubTokenKey(importKeyInput),
    [importKeyInput],
  );

  const handleImportSubmit = useCallback(() => {
    if (!importKeyInput.trim() || !decodedImportInfo) return;

    importHubItem(
      { importKey: importKeyInput.trim() },
      {
        onSuccess: () => {
          toast.add({
            title: t("hub.package_manager.import_success", {
              defaultValue: "Import successful",
            }),
            data: { type: "success" },
          });
          onOpenChange(false);
          form.reset();
          onSuccess?.();
        },
        onError: (error: unknown) => {
          const apiError = error as { status?: number; message?: string };
          toast.add({
            title: t("hub.package_manager.import_error", {
              defaultValue: "Import failed",
            }),
            ...(apiError.status === 409 && apiError.message
              ? { description: apiError.message }
              : {}),
            data: { type: "error" },
          });
        },
      },
    );
  }, [
    importKeyInput,
    decodedImportInfo,
    importHubItem,
    toast,
    t,
    onOpenChange,
    onSuccess,
  ]);

  return (
    <ConfirmAction
      open={open}
      onOpenChange={(newOpen) => {
        onOpenChange(newOpen);
        if (!newOpen) form.reset();
      }}
      title={
        title ||
        t("hub.package_manager.import_title", {
          defaultValue: "Import Packages or Catalogs",
        })
      }
      description={
        <div className="flex flex-col gap-4 w-full">
          <div>
            {description ||
              t("hub.package_manager.import_description", {
                defaultValue:
                  "Paste the token key to import packages or catalogs.",
              })}
          </div>
          <div className="grid gap-4 pb-4">
            <Form {...form}>
              <div className="grid gap-2">
                <FormInputField
                  control={form.control}
                  name="importToken"
                  type="password"
                  placeholder={t(
                    "hub.package_manager.import_token_placeholder",
                    {
                      defaultValue: "Paste token key here",
                    },
                  )}
                />
                {decodedImportInfo && (
                  <Item variant="muted" size="sm">
                    <ItemMedia
                      variant="icon"
                      className="text-success-foreground">
                      <CheckCircle2 className="size-4" />
                    </ItemMedia>
                    <ItemContent>
                      <ItemTitle>
                        {decodedImportInfo.type === "packages"
                          ? t(
                              "hub.package_manager.import_count_info_packages",
                              {
                                defaultValue: `Ready to import ${decodedImportInfo.count} package(s).`,
                                count: decodedImportInfo.count,
                              },
                            )
                          : t(
                              "hub.package_manager.import_count_info_catalogs",
                              {
                                defaultValue: `Ready to import ${decodedImportInfo.count} catalog(s).`,
                                count: decodedImportInfo.count,
                              },
                            )}
                      </ItemTitle>
                    </ItemContent>
                  </Item>
                )}
              </div>
            </Form>
          </div>
        </div>
      }
      cancelText={t("hub.administer.cancel", { defaultValue: "Cancel" })}
      confirmText={t("hub.package_manager.import_submit", {
        defaultValue: "Import",
      })}
      onConfirm={handleImportSubmit}
      disabled={!decodedImportInfo || isImportPending}
    />
  );
}
